'use client';

import { useId, type ReactNode } from 'react';

import styles from './chip-field.module.scss';

export type ChipFieldHeadingLevel = 1 | 2 | 3 | 4;

export type ChipFieldOption = {
  value: string;
  label: string;
  iconUrl?: string;
};

export type ChipFieldProps = {
  id?: string;
  label: string;
  options: ChipFieldOption[];
  selectedOptions: string | string[];
  onChange: (next: string | string[]) => void;
  multiSelect?: boolean;
  headingLevel?: ChipFieldHeadingLevel;
};

export function ChipField({
  id,
  label,
  options,
  selectedOptions,
  onChange,
  multiSelect,
  headingLevel
}: ChipFieldProps) {
  const reactId = useId();
  const baseId = id ?? `chip-field-${reactId}`;
  const labelId = `${baseId}-label`;

  const isSelected = (value: string) => {
    if (multiSelect) {
      return Array.isArray(selectedOptions) && selectedOptions.includes(value);
    }
    return selectedOptions === value;
  };

  const handleChipClick = (option: ChipFieldOption) => {
    if (multiSelect) {
      const current = Array.isArray(selectedOptions) ? selectedOptions : [];
      const next = current.includes(option.value)
        ? current.filter((item) => item !== option.value)
        : [...current, option.value];
      onChange(next);
      return;
    }

    onChange(option.value);
  };

  const HeadingTag = headingLevel
    ? (`h${headingLevel}` as React.ElementType)
    : null;

  const labelNode: ReactNode = (
    <>
      {label}{' '}
      {multiSelect ? (
        <span className={styles.note}>(Select all that apply)</span>
      ) : null}
    </>
  );

  return (
    <div className={styles.field}>
      {HeadingTag ? (
        <HeadingTag id={labelId} className={styles.label}>
          {labelNode}
        </HeadingTag>
      ) : (
        <div id={labelId} className={styles.label}>
          {labelNode}
        </div>
      )}

      <div className={styles.chips} role="group" aria-labelledby={labelId}>
        {options.map((option) => {
          const selected = isSelected(option.value);
          return (
            <button
              key={option.value}
              type="button"
              className={[styles.chip, selected ? styles.chipSelected : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => handleChipClick(option)}
              aria-pressed={selected}
              aria-label={`Select ${option.label}`}
            >
              {option.iconUrl ? (
                <img
                  src={option.iconUrl}
                  alt={option.label}
                  className={styles.icon}
                />
              ) : null}
              <span className={styles.text}>{option.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
